import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input } from 'components/common';
import { createNewsCategory, getAdminNewsCategoryById as getNewsCategoryById, updateNewsCategory } from 'apis/adminNewsCategories';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const NewsCategoriesForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        sortOrder: 0,
        isActive: true,
        translation: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: detail, error: detailError } = useAdminDetailQuery('news-categories', id, getNewsCategoryById);
    const createMutation = useAdminCreateMutation('news-categories', createNewsCategory);
    const updateMutation = useAdminUpdateMutation('news-categories', updateNewsCategory);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({ languageId: l.id, title: '', slug: '' })),
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) setFormData(detail);
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({
            ...prev,
            translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        try {
            const payload = { ...formData, sortOrder: Number(formData.sortOrder) };
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);
            if (res?.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/news-categories');
            } else {
                if (res?.messageError) setErrors(res.messageError);
                else toast.error(res?.message || 'Lưu thất bại');
            }
        } catch (err) {
            toast.error(err.message || 'Lưu thất bại');
        }
    };

    return (
        <div className="admin-page">
            <PageHeader
                title={id ? 'Cập nhật Danh Mục Tin Tức' : 'Thêm Danh Mục Tin Tức'}
                subtitle="Quản lý danh mục tin tức với tiêu đề và slug đa ngôn ngữ"
            />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt Danh mục</p>
                <div className="form-row">
                    <Input
                        label="Thứ tự sắp xếp"
                        name="sortOrder"
                        type="number"
                        value={formData.sortOrder}
                        onChange={handleChange}
                    />
                </div>
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button
                                        key={lang.id}
                                        type="button"
                                        className={`tab-btn ${activeTab === i ? 'active' : ''}`}
                                        onClick={() => setActiveTab(i)}
                                    >
                                        {lang.name} ({lang.code})
                                    </button>
                                ))}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input
                                            label="Tiêu đề *"
                                            value={t.title || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'title', e.target.value)}
                                            required
                                            error={errors[`translation.${lang.id}.title`]}
                                        />
                                        <Input
                                            label="Slug"
                                            value={t.slug || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'slug', e.target.value)}
                                            placeholder="e.g. tin-tuc-cong-ty"
                                        />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}

                <div className="form-actions">
                    <Button type="submit" disabled={loading}>
                        {loading ? 'Đang lưu...' : id ? 'Cập nhật Danh Mục' : 'Thêm Danh Mục'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/news-categories')}>
                        Hủy
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default NewsCategoriesForm;
